import React, { useState } from 'react';
import {
  Alert, Button, Collapse, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useOperations } from '../operations/OperationsProvider';
import { formatDateTime } from '../common/util/datetime';
import { challanOwed, CHALLAN_LABELS } from './challan';

// Records the stage a truck has physically reached. The trip's next expected stage comes first,
// so a normal pass through the gate is a single action; a skipped stage or a corrected time sits
// behind "More options", and a weighbridge asks for its reading and ticket.
const MilestonePanel = ({ trip }) => {
  const { mutate } = useOperations();
  const next = trip.checkpoints[trip.progress];
  const [checkpointId, setCheckpointId] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [more, setMore] = useState(false);
  // What this load still owes off its challan. Only ever a lorry that came in off the parking on
  // its number plate, and only until it is through the gate - so for everything else this is empty
  // and nothing below it appears.
  const owed = challanOwed(trip);
  const [paper, setPaper] = useState({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const target = trip.checkpoints.find((c) => String(c.id) === String(checkpointId)) || next;
  if (!target) return <Typography color="text.secondary">Every stage on this route is recorded.</Typography>;
  const weighing = target.type === 'WEIGHBRIDGE';
  const outOfOrder = next && target.id !== next.id;
  const record = async (event) => {
    event.preventDefault(); setPending(true); setError(null); setDone(null);
    try {
      const body = { checkpointId: target.id };
      // Typed here and sent with the stage, so the man at the gate does one thing at one counter.
      owed.forEach((key) => { if (String(paper[key] ?? '').trim()) body[key] = String(paper[key]).trim(); });
      if (weighing) { body.weightKg = Number(weightKg); body.ticketNumber = ticketNumber; }
      if (occurredAt) { body.occurredAt = new Date(occurredAt).toISOString(); body.correctionReason = correctionReason; }
      await mutate(`trips/${trip.id}/milestones`, body);
      setDone(`${target.name} recorded${occurredAt ? ` for ${formatDateTime(occurredAt)}` : ''}.`);
      setCheckpointId(''); setWeightKg(''); setTicketNumber(''); setOccurredAt(''); setCorrectionReason(''); setMore(false); setPaper({});
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  return (
    <Stack component="form" onSubmit={record} gap={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {done && <Alert severity="success">{done}</Alert>}
      <Typography variant="body2" color={outOfOrder ? 'warning.main' : 'text.secondary'}>
        {outOfOrder ? `Out of sequence: the route expects ${next.name} next. Recording this stage raises an exception.` : `Next stage · ${trip.progress + 1} of ${trip.checkpoints.length}`}
      </Typography>
      {/* The challan, for a lorry that was parked on its number plate alone. Past this barrier the
          load is weighed and tipped against this paper, so it is collected before the barrier goes
          up - and collected here, because here is where the lorry and the paper both are. */}
      {owed.length > 0 && (
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="warning.main">
            Taken into the parking without its challan. These are needed before it goes into the plant.
          </Typography>
          {owed.map((key) => (
            <TextField
              key={key}
              required
              fullWidth
              disabled={pending}
              label={CHALLAN_LABELS[key]}
              value={paper[key] || ''}
              onChange={(e) => setPaper((current) => ({ ...current, [key]: e.target.value }))}
              inputProps={{ maxLength: 500 }}
            />
          ))}
        </Stack>
      )}
      {weighing && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
          <TextField required label="Weighbridge reading (kg)" type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} inputProps={{ min: 1, max: 200000, step: 1 }} disabled={pending} sx={{ flex: 1 }} />
          <TextField required label="Weighbridge ticket number" value={ticketNumber} onChange={(e) => setTicketNumber(e.target.value)} inputProps={{ maxLength: 100 }} disabled={pending} sx={{ flex: 1 }} />
        </Stack>
      )}
      <Collapse in={more} unmountOnExit>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <TextField select label="Stage" value={checkpointId || next?.id || ''} onChange={(e) => setCheckpointId(e.target.value)} disabled={pending} helperText="Choose another stage only when the truck skipped one.">
            {trip.checkpoints.map((c, index) => <MenuItem key={c.id} value={c.id}>{`${index + 1}. ${c.name}`}</MenuItem>)}
          </TextField>
          <TextField label="Time reached" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} InputLabelProps={{ shrink: true }} disabled={pending} helperText="Leave empty to record the current time." />
          {occurredAt && <TextField required label="Reason for the corrected time" value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} inputProps={{ maxLength: 500 }} disabled={pending} />}
        </Stack>
      </Collapse>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Button type="submit" variant="contained" size="large" disabled={pending}>{pending ? 'Recording…' : `Record ${target.name}`}</Button>
        <Button onClick={() => setMore((open) => !open)} disabled={pending}>{more ? 'Fewer options' : 'More options'}</Button>
      </Stack>
    </Stack>
  );
};

export default MilestonePanel;
